import React, { useEffect, useState } from 'react';
import {
  ArrowsClockwise, ClockCounterClockwise, Sparkle, ArrowRight, Info, ArrowClockwise, Check, X,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import {
  getBranchNames, getGroupCode, getTickets, getPaymentAdjustmentDetails, getSubscriberDetails,
  updateAdjustmentDetails, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';

const EMPTY_EXISTING = {
  subscriberName: '', contactId: '', adjustmentAmount: '', paymentAdjustmentType: '',
  otherAdjustmentTypeId: '', chequeNumber: '',
};

function GroupCodeChange() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups, shared by both columns
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [newGroups, setNewGroups] = useState([]);
  const [newTicket, setNewTicket] = useState('');
  const [newTickets, setNewTickets] = useState([]);
  const [reason, setReason] = useState('');

  const [existing, setExisting] = useState(EMPTY_EXISTING);

  // resolved ids the save call needs
  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [newSchema, setNewSchema] = useState(null);
  const [newChitgroupId, setNewChitgroupId] = useState(null);
  const [newBranchId, setNewBranchId] = useState(null);
  const [newContactId, setNewContactId] = useState(null);
  const [newContactName, setNewContactName] = useState('');
  const [bpoChequeInformationId, setBpoChequeInformationId] = useState(null);
  const [bpoChequeInformationDetailId, setBpoChequeInformationDetailId] = useState(null);
  const [chitPaymentAdjustmentId, setChitPaymentAdjustmentId] = useState(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOldGroup, setPendingOldGroup] = useState('');
  const [pendingOldTicket, setPendingOldTicket] = useState('');
  const [pendingNewBranch, setPendingNewBranch] = useState('');
  const [pendingNewGroup, setPendingNewGroup] = useState('');
  const [pendingNewTicket, setPendingNewTicket] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  const noDataFound = (what) => showToast('warn', 'No Data Found', `No ${what} found for the current selection.`);

  useEffect(() => {
    getBranchNames()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setBranchlist(list);
        if (list.length === 0) noDataFound('branches');
      })
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromNewTicket = () => {
    setNewContactId(null);
    setNewContactName('');
  };

  const resetFromNewGroup = () => {
    setNewChitgroupId(null);
    setNewTicket('');
    setNewTickets([]);
    resetFromNewTicket();
  };

  const resetFromNewBranch = () => {
    setNewSchema(null);
    setNewBranchId(null);
    setNewGroup('');
    setNewGroups([]);
    resetFromNewGroup();
  };

  const resetFromTicket = () => {
    setExisting(EMPTY_EXISTING);
    setBpoChequeInformationId(null);
    setBpoChequeInformationDetailId(null);
    setChitPaymentAdjustmentId(null);
    setAdjustmentAmount(null);
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
    setNewBranch('');
    resetFromNewBranch();
  };

  const handleCaoChange = async (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;

    const selected = branchlist.find((x) => x.branch_name === value);
    if (!selected) return;

    setBranchschema(selected.branch_code);
    setBranchCode(selected.branch_code);

    try {
      const res = await getGroupCode(selected.branch_code);
      const list = Array.isArray(res) ? res : [];
      setBranchCodeList(list);
      if (list.length === 0) noDataFound('group codes');
    } catch (err) {
      console.error('[getGroupCode] error =', err);
      setBranchCodeList([]);
      showToast('error', 'Load Failed', 'Failed to load group codes. Please try again.');
    }
  };

  const handleGroupChange = async (value) => {
    setGroup(value || '');
    resetFromGroup();
    setNewBranch('');
    resetFromNewBranch();
    if (!value) return;

    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (!selected) return;

    setGroupCode(selected.group_Code);

    try {
      const res = await getTickets(branchschema, selected.group_Code);
      const list = Array.isArray(res) ? res : [];
      setTickets(list);
      if (list.length === 0) noDataFound('tickets');
    } catch (err) {
      console.error('[getTickets] error =', err);
      setTickets([]);
      showToast('error', 'Load Failed', 'Failed to load tickets. Please try again.');
    }
  };

  const handleTicketChange = async (value) => {
    setTicket(value === '' || value === null || value === undefined ? '' : value);
    resetFromTicket();
    if (value === '' || value === null || value === undefined) return;

    try {
      const res = await getPaymentAdjustmentDetails(branchschema, groupCode, value);
      if (!Array.isArray(res) || res.length === 0) {
        noDataFound('payment adjustment details');
        return;
      }

      const d = res[0];
      setBpoChequeInformationId(d.tbl_trans_bpo_cheques_information_id);
      setBpoChequeInformationDetailId(d.tbl_trans_bpo_cheques_information_details_id);
      setChitPaymentAdjustmentId(d.tbl_trans_chit_payment_adjustments_id);
      setAdjustmentAmount(d.adjustment_amount);

      setExisting({
        subscriberName: d.subscriber_name ?? '',
        contactId: d.tbl_mst_contact_id ?? '',
        adjustmentAmount: d.adjustment_amount ?? '',
        paymentAdjustmentType: d.payment_adjustment_type ?? '',
        otherAdjustmentTypeId: d.other_adjustment_type_id ?? '',
        chequeNumber: d.cheque_number ?? '',
      });
    } catch (err) {
      console.error('[getPaymentAdjustmentDetails] error =', err);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load payment adjustment details.'));
    }
  };

  // New side is fully independent: any branch, group code and ticket.
  const handleNewBranchChange = async (value) => {
    setNewBranch(value || '');
    resetFromNewBranch();
    if (!value) return;

    const selected = branchlist.find((x) => x.branch_name === value);
    if (!selected) return;

    setNewSchema(selected.branch_code);
    setNewBranchId(selected.tbl_mst_branch_configuration_id);

    try {
      const res = await getGroupCode(selected.branch_code);
      const list = Array.isArray(res) ? res : [];
      setNewGroups(list);
      if (list.length === 0) noDataFound('group codes');
    } catch (err) {
      console.error('[newBranch getGroupCode] error =', err);
      setNewGroups([]);
      showToast('error', 'Load Failed', 'Failed to load group codes for the new branch. Please try again.');
    }
  };

  const handleNewGroupChange = async (value) => {
    setNewGroup(value || '');
    resetFromNewGroup();
    if (!value) return;

    const selected = newGroups.find((x) => x.group_Code === value);
    if (!selected) return;

    setNewChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(newSchema, selected.group_Code);
      const list = Array.isArray(res) ? res : [];
      setNewTickets(list);
      if (list.length === 0) noDataFound('tickets');
    } catch (err) {
      console.error('[newGroup getTickets] error =', err);
      setNewTickets([]);
      showToast('error', 'Load Failed', 'Failed to load tickets for the new group. Please try again.');
    }
  };

  const handleNewTicketChange = async (value) => {
    setNewTicket(value === '' || value === null || value === undefined ? '' : value);
    resetFromNewTicket();
    if (value === '' || value === null || value === undefined) return;

    try {
      const res = await getSubscriberDetails(newSchema, newGroup, value);
      if (!Array.isArray(res) || res.length === 0) {
        noDataFound('subscriber for the new ticket');
        return;
      }
      setNewContactId(res[0].tbl_mst_contact_id);
      setNewContactName(res[0].subscriber_name ?? '');
    } catch (err) {
      console.error('[getSubscriberDetails] error =', err);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load the new ticket subscriber.'));
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && ticket === '';
  const newBranchInvalid = touched && !newBranch;
  const newGroupInvalid = touched && !newGroup;
  const newTicketInvalid = touched && newTicket === '';
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!cao && !!group && ticket !== '' && !!newBranch && !!newGroup && newTicket !== ''
    && reason.trim().length >= 10 && reason.length <= 200;

  // Step 1: validate + stage data, open confirm modal
  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!bpoChequeInformationDetailId || !chitPaymentAdjustmentId) {
      showToast('warn', 'Incomplete Form', 'No payment adjustment details found for the selected existing ticket.');
      return;
    }

    if (!newContactId) {
      showToast('warn', 'Incomplete Form', 'Could not resolve the subscriber for the selected new ticket. Please re-select.');
      return;
    }

    if (!Number.isFinite(Number(newBranchId)) || Number(newBranchId) <= 0) {
      showToast('warn', 'No Data Found', 'No branch id found for the selected new branch. Please re-select.');
      return;
    }

    // adjustmentChitGroupId binds to a non-nullable long, and GetGroupcodes returns
    // chitgroup_id as a string that is '' when the column is NULL.
    if (!Number.isFinite(Number(newChitgroupId)) || Number(newChitgroupId) <= 0) {
      showToast('warn', 'No Data Found', 'No chit group id found for the selected new group. Please re-select.');
      return;
    }

    setPendingOldGroup(group);
    setPendingOldTicket(ticket);
    setPendingNewBranch(newBranch);
    setPendingNewGroup(newGroup);
    setPendingNewTicket(newTicket);
    setPendingReason(reason.trim());

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  // Step 2: fires only after the user clicks "Yes, Save" in the modal
  const confirmSave = async () => {
    setSaving(true);

    const payload = {
      branchschema: branchCode, // schema that holds the adjustment rows
      adjustmentChitGroupId: Number(newChitgroupId),
      adjustmentBranchId: Number(newBranchId),
      adjustmentChitGroupCode: newGroup,
      oldAdjustmentTicketNo: Number(ticket),
      adjustmentTicketNo: Number(newTicket),
      adjustmentContactId: Number(newContactId),
      bpoChequeInformationIds: String(bpoChequeInformationId ?? ''),
      bpoChequeInformationDetailIds: String(bpoChequeInformationDetailId ?? ''),
      adjustmentAmount,
      chitPaymentAdjustmentIds: String(chitPaymentAdjustmentId ?? ''),
    };

    try {
      const res = await updateAdjustmentDetails(payload);
      if (res && res.success === false) {
        setSaving(false);
        setShowConfirmModal(false);
        showToast('error', 'Update Failed', res.message || 'Could not update the Group Code change. Please try again.', true);
        return;
      }

      const oldData = {
        branchCode,
        groupCode: group,
        ticketNo: ticket,
        subscriberName: existing.subscriberName,
        contactId: existing.contactId,
        adjustmentAmount: existing.adjustmentAmount,
        paymentAdjustmentType: existing.paymentAdjustmentType,
        otherAdjustmentTypeId: existing.otherAdjustmentTypeId,
        chequeNumber: existing.chequeNumber,
        bpoChequeInformationId,
        bpoChequeInformationDetailId,
        chitPaymentAdjustmentId,
      };

      const newData = {
        branchSchema: newSchema,
        branchName: newBranch,
        branchId: newBranchId,
        groupCode: newGroup,
        chitgroupId: newChitgroupId,
        ticketNo: newTicket,
        contactId: newContactId,
        contactName: newContactName,
      };

      const audit = {
        schemaName: branchschema,
        loginName,
        loginTime: new Date().toISOString(),
        changeType: 'CHIT_PAYMENT_ADJUSTMENT_GROUP_CODE_CHANGE',
        oldData,
        newData,
        reason: reason.trim(),
      };

      try {
        await saveData([audit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Group Code change updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Record updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the Group Code change. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setShowConfirmModal(false);
    setPendingOldGroup('');
    setPendingOldTicket('');
    setPendingNewBranch('');
    setPendingNewGroup('');
    setPendingNewTicket('');
    setPendingReason('');
  };

  const ro = { fontSize: '13px', background: '#F9FAFB' };
  const smLabel = { display: 'block', fontSize: '11px', marginBottom: '2px' };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="group-code-change-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="group-code-change-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowsClockwise size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Adjustment</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Group Code Change</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Existing Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Existing Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load payment adjustment details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="group-code-change-cao"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="group-code-change-group"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="group-code-change-ticket"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={ticket === ''} hint="Select a ticket in section A first">
        {/* SECTION B: Existing vs New */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Group Code Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Review existing details and select the new branch / group code / ticket</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'start' }}>
          {/* OLD COLUMN */}
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', background: '#F9FAFB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '10px' }}>
              <ClockCounterClockwise size={13} /> Existing Details
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={smLabel}>Subscriber Name</label>
                <input className="input-field" style={ro} value={existing.subscriberName} readOnly placeholder="—" data-testid="group-code-change-subscriber" />
              </div>
              <div>
                <label style={smLabel}>Contact ID</label>
                <input className="input-field" style={ro} value={existing.contactId} readOnly placeholder="—" data-testid="group-code-change-contact-id" />
              </div>
              <div>
                <label style={smLabel}>Adjustment Amount</label>
                <input className="input-field" style={ro} value={existing.adjustmentAmount} readOnly placeholder="—" data-testid="group-code-change-amount" />
              </div>
              <div>
                <label style={smLabel}>Payment Adjustment Type</label>
                <input className="input-field" style={ro} value={existing.paymentAdjustmentType} readOnly placeholder="—" />
              </div>
              <div>
                <label style={smLabel}>Other Adjustment Type ID</label>
                <input className="input-field" style={ro} value={existing.otherAdjustmentTypeId} readOnly placeholder="—" />
              </div>
              <div>
                <label style={smLabel}>Cheque Number</label>
                <input className="input-field" style={ro} value={existing.chequeNumber} readOnly placeholder="—" />
              </div>
            </div>
          </div>

          {/* ARROW */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '60px', color: '#9CA3AF' }}>
            <ArrowRight size={18} />
            <span style={{ fontSize: '10px', marginTop: '2px' }}>Change To</span>
          </div>

          {/* NEW COLUMN */}
          <div style={{ border: '1px solid #BFDBFE', borderRadius: '8px', padding: '14px', background: '#F8FAFF' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '10px' }}>
              <Sparkle size={13} /> New Details
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>EDITABLE</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={smLabel}>New Branch Name <span style={{ color: '#DC2626' }}>*</span></label>
                <SearchableSelect
                  key={`new-branch-${formKey}`}
                  options={branchlist}
                  getLabel={(o) => o.branch_name}
                  getValue={(o) => o.branch_name}
                  value={newBranch}
                  onChange={handleNewBranchChange}
                  placeholder="Select new branch"
                  emptyMessage="No branches found"
                  disabled={false}
                  error={newBranchInvalid}
                  testId="group-code-change-new-branch"
                />
                {newBranchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New Branch is required</div>}
              </div>
              <div>
                <label style={smLabel}>New Group Code <span style={{ color: '#DC2626' }}>*</span></label>
                <SearchableSelect
                  key={`new-group-${formKey}`}
                  options={newGroups}
                  getLabel={(o) => o.group_Code}
                  getValue={(o) => o.group_Code}
                  value={newGroup}
                  onChange={handleNewGroupChange}
                  placeholder="Select new group code"
                  emptyMessage="Select a branch first"
                  disabled={!newSchema}
                  error={newGroupInvalid}
                  testId="group-code-change-new-group"
                />
                {newGroupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New Group Code is required</div>}
              </div>
              <div>
                <label style={smLabel}>New Ticket No. <span style={{ color: '#DC2626' }}>*</span></label>
                <SearchableSelect
                  key={`new-ticket-${formKey}`}
                  options={newTickets}
                  getLabel={(o) => String(o.ticketno)}
                  getValue={(o) => o.ticketno}
                  value={newTicket}
                  onChange={handleNewTicketChange}
                  placeholder="Select new ticket no"
                  emptyMessage="Select a group code first"
                  disabled={!newGroup}
                  error={newTicketInvalid}
                  testId="group-code-change-new-ticket"
                />
                {newTicketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New Ticket No. is required</div>}
              </div>
              <div>
                <label style={smLabel}>Contact Name</label>
                <input className="input-field" style={ro} value={newContactName} readOnly placeholder="—" data-testid="group-code-change-new-contact-name" />
              </div>
              <div>
                <label style={smLabel}>Contact ID</label>
                <input className="input-field" style={ro} value={newContactId ?? ''} readOnly placeholder="—" data-testid="group-code-change-new-contact-id" />
              </div>
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={ticket === '' || !newBranch || !newGroup || newTicket === ''} hint="Select the new branch, group code and ticket in section B first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="group-code-change-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Minimum 10 characters required'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="group-code-change-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="group-code-change-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '460px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="group-code-change-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ArrowsClockwise size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Group Code Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to move this payment adjustment to another branch, group code and ticket. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Existing Group / Ticket</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldGroup} / {pendingOldTicket}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Branch / Group / Ticket</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewBranch} / {pendingNewGroup} / {pendingNewTicket}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="group-code-change-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="group-code-change-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupCodeChange;
